import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { createPortal } from "react-dom";
import { NavLink, useNavigate } from "react-router-dom";
import "./SuiviProduction.css";

const SuiviProduction = () => {
    const navigate = useNavigate();

    // --- CONFIGURATION ---
    const currentUser = { name: "Admin", role: "Admin" };
    const ALLOWED_BILLING_USERS = ["Admin", "Comptable"];
    const canAccessBilling = ALLOWED_BILLING_USERS.includes(currentUser.name);

    const etapesList = [
        { id: "1", title: "En attente d'affectation", category: "common" },
        { id: "2", title: "En cours", category: "common" },
        { id: "300", title: "Traité", category: "common" },
        { id: "400", title: "Facturation", category: "common" }
    ];

    // --- ÉTATS ---
    const [columns, setColumns] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTunnel, setActiveTunnel] = useState("alarme");

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [closingModal, setClosingModal] = useState(null);
    const [responsables, setResponsables] = useState([]);
    const [editingItem, setEditingItem] = useState(null);

    const [expandedNoteId, setExpandedNoteId] = useState(null);

    const [newSuivi, setNewSuivi] = useState({
        client_nom: "",
        objet_inter: "",
        id_etape: "1",
        id_responsable: "",
        is_urgent: 0,
        date_echeance: "",
        num_devis: "",
        montant_chantier: "",
        type_tunnel: "alarme",
        num_facture: "",
        notes: ""
    });

    // --- API CALLS ---
    const fetchResponsables = async () => {
        try {
            const res = await fetch("https://aurelien.kanban.amservices.eu/api/get_responsables.php");
            const data = await res.json();
            if (data.success) {
                setResponsables(data.data);
                if (data.data.length > 0) setNewSuivi(prev => ({ ...prev, id_responsable: data.data[0].id }));
            }
        } catch (err) { console.error(err); }
    };

    const fetchSuivis = async () => {
        try {
            const res = await fetch("https://aurelien.kanban.amservices.eu/api/get_suivis.php");
            const data = await res.json();
            if (data.success) {
                const newColumns = {};
                etapesList.forEach(et => { newColumns[et.id] = { ...et, items: [] }; });
                data.data.forEach(item => {
                    const stepId = String(item.id_etape);
                    if (newColumns[stepId]) newColumns[stepId].items.push(item);
                });
                setColumns(newColumns);
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    useEffect(() => {
        fetchResponsables();
        fetchSuivis();
    }, []);

    const toggleUrgent = async (e, item) => {
        e.stopPropagation();
        const newStatus = item.is_urgent == 1 ? 0 : 1;
        const updatedItem = { ...item, is_urgent: newStatus };

        const newCols = { ...columns };
        const colId = String(item.id_etape);
        const itemIndex = newCols[colId].items.findIndex(i => i.id === item.id);
        if (itemIndex > -1) {
            newCols[colId].items[itemIndex] = updatedItem;
            setColumns({ ...newCols });
        }

        try {
            await fetch("https://aurelien.kanban.amservices.eu/api/update_full_suivi.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatedItem)
            });
        } catch (err) {
            console.error("Erreur update urgent", err);
            fetchSuivis();
        }
    };

    const toggleNoteView = (e, itemId) => {
        e.stopPropagation();
        setExpandedNoteId(expandedNoteId === itemId ? null : itemId);
    };

    // --- LOGIQUE ---
    const sortItems = (items) => {
        return [...items].sort((a, b) => {
            if (b.is_urgent !== a.is_urgent) return b.is_urgent - a.is_urgent;
            return new Date(a.date_echeance || "9999-12-31") - new Date(b.date_echeance || "9999-12-31");
        });
    };

    const handleCloseModal = (modalName, setModalState) => {
        setClosingModal(modalName);
        setTimeout(() => { setModalState(false); setClosingModal(null); }, 280);
    };

    const handleAddSuivi = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch("https://aurelien.kanban.amservices.eu/api/add_suivi.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newSuivi)
            });
            if ((await res.json()).success) {
                handleCloseModal('new', setShowModal);
                setNewSuivi({ ...newSuivi, client_nom: "", objet_inter: "", montant_chantier: "", num_devis: "", notes: "" });
                fetchSuivis();
            }
        } catch (err) { console.error(err); }
    };

    const handleUpdateSuivi = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch("https://aurelien.kanban.amservices.eu/api/update_full_suivi.php", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editingItem)
            });
            if ((await res.json()).success) {
                handleCloseModal('edit', setShowEditModal);
                fetchSuivis();
            }
        } catch (err) { console.error(err); }
    };

    const handleDeleteSuivi = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce dossier ? Cette action est irréversible.")) {
            return;
        }

        try {
            const res = await fetch("https://aurelien.kanban.amservices.eu/api/delete_suivi.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: id })
            });

            const data = await res.json();
            if (data.success) {
                handleCloseModal('edit', setShowEditModal);
                fetchSuivis();
            } else {
                alert("Erreur lors de la suppression côté serveur.");
            }
        } catch (err) {
            console.error(err);
            alert("Une erreur de communication est survenue.");
        }
    };

    const onDragEnd = async (result) => {
        const { source, destination } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;

        const itemToMove = columns[source.droppableId].items[source.index];
        const newCols = { ...columns };

        newCols[source.droppableId].items.splice(source.index, 1);

        // --- CAS 1 : Déplacement classique ---
        if (destination.droppableId !== "400" && destination.droppableId !== "trash") {
            newCols[destination.droppableId].items.push({ ...itemToMove, id_etape: destination.droppableId });
            newCols[destination.droppableId].items = sortItems(newCols[destination.droppableId].items);
            setColumns(newCols);

            try {
                await fetch("https://aurelien.kanban.amservices.eu/api/update_etape.php", {
                    method: "POST", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: itemToMove.id, id_etape: destination.droppableId })
                });
            } catch (err) { fetchSuivis(); }
        }

        // --- CAS 2 : Bascule vers Facturation ---
        else if (destination.droppableId === "400") {
            setColumns(newCols);

            try {
                const res = await fetch("https://aurelien.kanban.amservices.eu/api/transfert_facturation.php", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: itemToMove.id })
                });

                const data = await res.json();
                if (!data.success) {
                    console.error("Erreur côté serveur lors du transfert.");
                    fetchSuivis();
                }
            } catch (err) {
                console.error("Erreur réseau", err);
                fetchSuivis();
            }
        }

        // --- CAS 3 : Corbeille ---
        else if (destination.droppableId === "trash") {
            setColumns(newCols); // retire visuellement
            if (window.confirm(`Supprimer le dossier "${itemToMove.client_nom}" ? Cette action est irréversible.`)) {
                try {
                    await fetch("https://aurelien.kanban.amservices.eu/api/delete_suivi.php", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ id: itemToMove.id })
                    });
                } catch (err) { console.error(err); }
            }
            fetchSuivis(); // dans tous les cas, rafraîchit (annuler = réapparition)
        }
    };

    const getFilteredItems = (items) => {
        if (!searchTerm) return items;
        const low = searchTerm.toLowerCase();
        return items.filter(i =>
            i.client_nom.toLowerCase().includes(low) ||
            (i.num_devis && i.num_devis.toLowerCase().includes(low))
        );
    };

    const totalCA = Object.values(columns).reduce((acc, col) =>
        acc + col.items.reduce((sum, item) => sum + parseFloat(item.montant_chantier || 0), 0), 0
    );

    const renderCard = (item, index) => (
        <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index}>
            {(provided, snapshot) => {
                const card = (
                    <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                        className={`kanban-card ${item.is_urgent == 1 ? "urgent" : ""} type-${item.type_tunnel}`}>

                        <div className="card-header">
                            <strong>
                                {item.client_nom}
                                {item.notes && (
                                    <span
                                        onClick={(e) => toggleNoteView(e, item.id)}
                                        className={`note-icon ${expandedNoteId === item.id ? 'active' : ''}`}
                                        title="Voir/Masquer la note"
                                    >
                                        📝
                                    </span>
                                )}
                            </strong>
                            <div className="card-actions">
                                <button
                                    className={`btn-action2 btn-urgent ${item.is_urgent == 1 ? "active" : ""}`}
                                    onClick={(e) => toggleUrgent(e, item)}
                                    title="Marquer comme urgent"
                                >
                                    {item.is_urgent == 1 ? "⚡" : "⚪"}
                                </button>
                                <button
                                    className="btn-action2"
                                    onClick={() => { setEditingItem(item); setShowEditModal(true); }}
                                    title="Modifier le dossier"
                                >
                                    ✏️
                                </button>
                            </div>
                        </div>

                        <div className="card-body">
                            <p className="objet-text" style={{ marginBottom: item.num_devis ? '4px' : '12px' }}>
                                🛠 {item.objet_inter || "Pas d'objet"}
                            </p>
                            {item.num_devis && (
                                <p className="devis-text" style={{ fontSize: '0.8rem', color: '#888', margin: '0 0 10px 0', fontStyle: 'italic' }}>
                                    📄 Devis : <strong>{item.num_devis}</strong>
                                </p>
                            )}

                            {expandedNoteId === item.id && item.notes && (
                                <div className="card-note-content">
                                    <strong>Note interne :</strong>
                                    <p>{item.notes}</p>
                                </div>
                            )}

                            <div className="card-meta">
                                <span className="date-info">
                                    📅 {new Date(item.date_echeance).toLocaleDateString('fr-FR')}
                                </span>
                                <span className="price-tag">
                                    {parseFloat(item.montant_chantier || 0).toFixed(2)}€
                                </span>
                            </div>
                        </div>
                    </div>
                );
                return snapshot.isDragging ? createPortal(card, document.body) : card;
            }}
        </Draggable>
    );

    if (loading) return <div className="admin-page">Chargement @m Services...</div>;

    return (
        <div className="admin-page">
            <div className="header-kanban">
                <div className="header-top">
                    <h1>Suivi de Production</h1>
                    <div className="ca-badge">Total Chantiers : {totalCA.toLocaleString()} €</div>
                </div>

                <div className="kanban-controls">
                    <div className="search-bar-container">
                        <input type="text" placeholder="Recherche client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
                    </div>
                    <button className="btn-add" onClick={() => setShowModal(true)}>+ Nouveau Dossier</button>
                </div>
            </div>

            <div className="kanban-board">
                <DragDropContext onDragEnd={onDragEnd}>
                    {etapesList
                        .filter(et => (et.category === "common" || et.category === activeTunnel) && et.id !== "400")
                        .map(et => {
                            const col = columns[et.id];
                            if (!col) return null;
                            const filtered = getFilteredItems(col.items);
                            return (
                                <div className="kanban-column" key={et.id}>
                                    <h2>{et.title} <span className="count">{filtered.length}</span></h2>
                                    <Droppable droppableId={et.id}>
                                        {(provided) => (
                                            <div {...provided.droppableProps} ref={provided.innerRef} className="kanban-column-content">
                                                {filtered.map((item, idx) => renderCard(item, idx))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </div>
                            );
                        })}

                    {!searchTerm && canAccessBilling && (
                        <Droppable droppableId="400">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`billing-trigger-zone ${snapshot.isDraggingOver ? "active-drop" : ""}`}
                                >
                                    <div className="billing-icon-large">🏭</div>
                                    <span>Kanban Production</span>
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}

                    {/* ZONE CORBEILLE */}
                    {!searchTerm && (
                        <Droppable droppableId="trash">
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className={`trash-drop-zone ${snapshot.isDraggingOver ? "active-drop" : ""}`}
                                >
                                    <div className="trash-icon-large">
                                        {snapshot.isDraggingOver ? "🗑️" : "🗑"}
                                    </div>
                                    <span>Supprimer</span>
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    )}
                </DragDropContext>
            </div>

            {/* MODALE CRÉATION */}
            {showModal && (
                <div className={`modal-overlay ${closingModal === 'new' ? 'closing' : ''}`}>
                    <div className="modal-content">
                        <h2>Nouveau Dossier Am Services</h2>
                        <form onSubmit={handleAddSuivi}>
                            <div className="form-group">
                                <label>Tunnel</label>
                                <select value={newSuivi.type_tunnel} onChange={(e) => setNewSuivi({ ...newSuivi, type_tunnel: e.target.value })}>
                                    <option value="alarme">Général</option>
                                    <option value="telecom">Télécom</option>
                                </select>
                            </div>
                            <div className="form-group"><label>Client</label><input type="text" required onChange={(e) => setNewSuivi({ ...newSuivi, client_nom: e.target.value })} /></div>
                            <div className="form-group"><label>Objet</label><input type="text" onChange={(e) => setNewSuivi({ ...newSuivi, objet_inter: e.target.value })} /></div>
                            <div className="form-row">
                                <div className="form-group"><label>N° Devis</label><input type="text" onChange={(e) => setNewSuivi({ ...newSuivi, num_devis: e.target.value })} /></div>
                                <div className="form-group"><label>Montant (€)</label><input type="number" step="0.01" onChange={(e) => setNewSuivi({ ...newSuivi, montant_chantier: e.target.value })} /></div>
                            </div>
                            <div className="form-group">
                                <label>Responsable</label>
                                <select value={newSuivi.id_responsable} onChange={(e) => setNewSuivi({ ...newSuivi, id_responsable: e.target.value })}>
                                    {responsables.map(res => <option key={res.id} value={res.id}>{res.nom}</option>)}
                                </select>
                            </div>
                            <div className="form-group"><label>Échéance</label><input type="date" required onChange={(e) => setNewSuivi({ ...newSuivi, date_echeance: e.target.value })} /></div>

                            <div className="form-group">
                                <label>Notes / Commentaires</label>
                                <textarea
                                    placeholder="Infos supplémentaires..."
                                    onChange={(e) => setNewSuivi({ ...newSuivi, notes: e.target.value })}
                                />
                            </div>

                            <label className="custom-checkbox">
                                <input type="checkbox" onChange={(e) => setNewSuivi({ ...newSuivi, is_urgent: e.target.checked ? 1 : 0 })} />
                                <span className="checkmark"></span> Urgent
                            </label>
                            <div className="modal-btns">
                                <button type="submit" className="btn-add">Créer Dossier</button>
                                <button type="button" onClick={() => handleCloseModal('new', setShowModal)}>Annuler</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODALE ÉDITION */}
            {showEditModal && editingItem && (
                <div className={`modal-overlay ${closingModal === 'edit' ? 'closing' : ''}`}>
                    <div className="modal-content">
                        <h2>Modifier Dossier</h2>
                        <form onSubmit={handleUpdateSuivi}>
                            <div className="form-group"><label>Client</label><input type="text" value={editingItem.client_nom} onChange={(e) => setEditingItem({ ...editingItem, client_nom: e.target.value })} /></div>
                            <div className="form-group"><label>Objet</label><input type="text" value={editingItem.objet_inter || ""} onChange={(e) => setEditingItem({ ...editingItem, objet_inter: e.target.value })} /></div>
                            <div className="form-row">
                                <div className="form-group"><label>Devis</label><input type="text" value={editingItem.num_devis || ""} onChange={(e) => setEditingItem({ ...editingItem, num_devis: e.target.value })} /></div>
                                <div className="form-group"><label>Montant</label><input type="number" value={editingItem.montant_chantier || ""} onChange={(e) => setEditingItem({ ...editingItem, montant_chantier: e.target.value })} /></div>
                            </div>

                            <div className="form-group">
                                <label>Notes internes</label>
                                <textarea
                                    value={editingItem.notes || ""}
                                    onChange={(e) => setEditingItem({ ...editingItem, notes: e.target.value })}
                                    placeholder="Ajouter une note..."
                                />
                            </div>

                            <div className="modal-btns">
                                <button
                                    className="supp_dossier"
                                    type="button"
                                    onClick={() => handleDeleteSuivi(editingItem.id)}
                                >
                                    Supprimer
                                </button>
                                <button type="submit" className="btn-add">Enregistrer</button>
                                <button type="button" onClick={() => handleCloseModal('edit', setShowEditModal)}>Fermer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuiviProduction;
